import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Check,
  CheckSquare,
  Download,
  Eye,
  EyeOff,
  GripVertical,
  ImageOff,
  Layers,
  Loader2,
  Move,
  RefreshCw,
  Square,
  Trash2,
  X as IconX,
} from "lucide-react";
import { toast } from "sonner";

import AdminAuthGate from "@/components/admin/AdminAuthGate";

// -----------------------------------------------------------------------------
//  AdminCuratedGalleriesPage — /admin/curated-galleries
//
//  Manage the per-accommodation Curated Gallery. Each hotel has an
//  independent collection of image references. On first access the operator
//  clicks "Import from Bunny" to clone every image URL found in the hotel's
//  Bunny Storage folder into the curated gallery (one-time reference copy).
//  From then on, adds/removes here NEVER touch Bunny — the two systems live
//  side-by-side.
//
//  Data source (single):
//    GET  /api/admin/curated-galleries                  → summary rows
//    GET  /api/admin/curated-galleries/{hotel_slug}     → images
//    POST /api/admin/curated-galleries/{slug}/import[?replace=true]
//    DEL  /api/admin/curated-galleries/{slug}/images/{image_id}
//    DEL  /api/admin/curated-galleries/{slug}           → clear all
// -----------------------------------------------------------------------------

const API = process.env.REACT_APP_BACKEND_URL || "";
const PAGE_SIZE = 60; // grid becomes noisy above ~60 tiles per screen

const fetcher = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err = new Error(data?.detail?.message || data?.detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.detail = data?.detail;
    throw err;
  }
  return data;
};

// ─────────────────────────────────────────────────────────────────────────
//  Hotel summary row
// ─────────────────────────────────────────────────────────────────────────
const SummaryRow = ({ hotel, onOpen, onImport, isImporting }) => (
  <tr
    className="border-b border-[#E8DFCF] hover:bg-[#FDFBF7] transition-colors"
    data-testid={`curated-summary-row-${hotel.hotel_slug}`}
  >
    <td className="px-4 py-3">
      <button
        type="button"
        onClick={() => onOpen(hotel)}
        className="text-left font-serif-x text-[15px] text-[#2C2621] hover:text-[#C16542] transition-colors"
        data-testid={`curated-open-${hotel.hotel_slug}`}
      >
        {hotel.hotel_slug}
      </button>
      <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#5C5248]/70">
        {hotel.bunny_folder}
      </div>
    </td>
    <td className="px-4 py-3 text-center">
      <div className="inline-flex flex-col items-center gap-1">
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] tracking-wide ${
            hotel.image_count > 0
              ? "bg-[#5A6B4F]/10 text-[#5A6B4F]"
              : "bg-[#C16542]/10 text-[#C16542]"
          }`}
        >
          {hotel.image_count > 0 ? <Check className="w-3 h-3" /> : <Layers className="w-3 h-3" />}
          {hotel.image_count}
        </span>
        {hotel.hidden_count > 0 ? (
          <span
            className="inline-flex items-center gap-1 text-[9px] tracking-[0.14em] uppercase text-[#C16542]"
            data-testid={`curated-hidden-count-${hotel.hotel_slug}`}
          >
            <EyeOff className="w-2.5 h-2.5" strokeWidth={2} /> {hotel.hidden_count} hidden
          </span>
        ) : null}
      </div>
    </td>
    <td className="px-4 py-3 text-right">
      {hotel.image_count === 0 ? (
        <button
          type="button"
          onClick={() => onImport(hotel)}
          disabled={isImporting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase bg-[#C16542] text-white hover:bg-[#A35133] disabled:opacity-60 transition-colors"
          data-testid={`curated-import-${hotel.hotel_slug}`}
        >
          {isImporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
          Import from Bunny
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onOpen(hotel)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-[#2C2621] text-[#2C2621] hover:bg-[#2C2621] hover:text-white transition-colors"
          data-testid={`curated-manage-${hotel.hotel_slug}`}
        >
          Manage
        </button>
      )}
    </td>
  </tr>
);

// ─────────────────────────────────────────────────────────────────────────
//  Image tile (selection · hide/unhide · delete · drag reorder)
// ─────────────────────────────────────────────────────────────────────────
const ImageTile = ({
  image,
  onDelete,
  onToggleHidden,
  onToggleSelect,
  isDeleting,
  isToggling,
  isSelected,
  selectionActive,
  reorderMode,
  isDragging,
  isDropTarget,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
}) => {
  const hidden = !!image.hidden;
  const handleTileClick = (e) => {
    if (reorderMode) return; // No clicks in reorder mode — dragging only.
    if (selectionActive) {
      e.preventDefault();
      onToggleSelect(image);
    }
  };

  return (
    <div
      className={`group relative aspect-square overflow-hidden bg-[#F2EBE1] border transition-all ${
        isSelected
          ? "border-[#2C2621] ring-2 ring-[#2C2621]"
          : hidden
          ? "border-[#C16542]/60"
          : "border-[#E8DFCF]"
      } ${selectionActive && !reorderMode ? "cursor-pointer" : ""} ${
        reorderMode ? "cursor-grab active:cursor-grabbing" : ""
      } ${isDragging ? "opacity-40" : ""} ${
        isDropTarget ? "ring-4 ring-[#C16542]" : ""
      }`}
      draggable={reorderMode}
      onClick={handleTileClick}
      onDragStart={reorderMode ? (e) => onDragStart(e, image) : undefined}
      onDragEnter={reorderMode ? (e) => onDragEnter(e, image) : undefined}
      onDragOver={reorderMode ? (e) => e.preventDefault() : undefined}
      onDragEnd={reorderMode ? onDragEnd : undefined}
      onDrop={reorderMode ? (e) => onDrop(e, image) : undefined}
      data-testid={`curated-tile-${image.id}`}
      data-hidden={hidden ? "true" : "false"}
      data-selected={isSelected ? "true" : "false"}
    >
      <img
        src={image.url}
        alt={image.name || ""}
        loading="lazy"
        decoding="async"
        draggable={false}
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
          !reorderMode ? "group-hover:scale-[1.03]" : ""
        } ${hidden ? "grayscale opacity-50" : ""} ${
          isSelected ? "brightness-90" : ""
        }`}
        onError={(e) => {
          e.currentTarget.style.opacity = "0.35";
        }}
      />

      {/* Grip icon — only in reorder mode, centered as a hint. */}
      {reorderMode ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <GripVertical className="w-6 h-6 text-white drop-shadow-lg" strokeWidth={2} />
        </div>
      ) : null}

      {/* Selection checkbox — hidden when reordering to keep the UI focused. */}
      {!reorderMode ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(image);
          }}
          title={isSelected ? "Unselect" : "Select"}
          className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-sm flex items-center justify-center shadow-md transition-all ${
            isSelected
              ? "bg-[#2C2621] text-white opacity-100"
              : "bg-white/95 text-[#2C2621] opacity-0 group-hover:opacity-100 focus:opacity-100"
          } ${selectionActive ? "opacity-100" : ""}`}
          data-testid={`curated-select-${image.id}`}
          aria-label={isSelected ? "Unselect image" : "Select image"}
          aria-pressed={isSelected}
        >
          {isSelected ? <CheckSquare className="w-4 h-4" strokeWidth={2} /> : <Square className="w-4 h-4" strokeWidth={2} />}
        </button>
      ) : null}

      {hidden ? (
        <div className={`absolute top-1.5 ${reorderMode ? "left-1.5" : "left-9"} inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#C16542] text-white text-[9px] tracking-[0.18em] uppercase shadow-sm`}>
          <EyeOff className="w-2.5 h-2.5" strokeWidth={2} /> Hidden
        </div>
      ) : null}

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

      {/* Action cluster — hide/unhide + delete, top-right (hidden in reorder mode). */}
      {!reorderMode ? (
        <div className="absolute top-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleHidden(image); }}
            disabled={isToggling || isDeleting}
            title={hidden ? "Unhide — show on public gallery" : "Hide — remove from public gallery"}
            className="w-7 h-7 rounded-full bg-white/95 hover:bg-[#2C2621] text-[#2C2621] hover:text-white flex items-center justify-center shadow-md transition-all disabled:opacity-60 disabled:cursor-wait"
            data-testid={`curated-toggle-visibility-${image.id}`}
            aria-label={hidden ? "Unhide image" : "Hide image"}
          >
            {isToggling ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : hidden ? (
              <Eye className="w-3.5 h-3.5" />
            ) : (
              <EyeOff className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(image); }}
            disabled={isDeleting || isToggling}
            title={image.name || "Remove from gallery"}
            className="w-7 h-7 rounded-full bg-white/95 hover:bg-[#C16542] text-[#2C2621] hover:text-white flex items-center justify-center shadow-md transition-all disabled:opacity-60 disabled:cursor-wait"
            data-testid={`curated-delete-${image.id}`}
            aria-label={`Delete ${image.name || "image"} from curated gallery`}
          >
            {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <IconX className="w-3.5 h-3.5" />}
          </button>
        </div>
      ) : null}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 text-[9px] tracking-wide text-white/90 opacity-0 group-hover:opacity-100 transition-opacity truncate">
        {image.name || image.path}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Detail view
// ─────────────────────────────────────────────────────────────────────────
const DetailView = ({ hotel, onBack, onSummaryRefresh }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [reimporting, setReimporting] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filter, setFilter] = useState("all");
  // Multi-select state — a Set gives O(1) lookup for tile render, and we
  // pass `selectionActive` through so the checkboxes stay visible while
  // any selection exists (better discoverability than pure hover).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Drag-and-drop reorder state. `reorderMode` is a toggle so ordinary
  // browsing never triggers accidental drags. `draggingId` is the id of the
  // tile currently being dragged; `dropTargetId` is the tile the cursor is
  // hovering over — it renders an orange ring to indicate "drop before me".
  const [reorderMode, setReorderMode] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetcher(`/api/admin/curated-galleries/${hotel.hotel_slug}`);
      setDoc(data);
      setVisibleCount(PAGE_SIZE);
    } catch (err) {
      toast.error(err.message || "Failed to load curated gallery");
    } finally {
      setLoading(false);
    }
  }, [hotel.hotel_slug]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (image) => {
    if (deletingId) return;
    if (!window.confirm(`Remove this image from the curated gallery?\n\n${image.name || image.path}\n\nBunny Storage will NOT be affected — the original file stays intact.`)) return;
    setDeletingId(image.id);
    try {
      const data = await fetcher(
        `/api/admin/curated-galleries/${hotel.hotel_slug}/images/${image.id}`,
        { method: "DELETE" }
      );
      setDoc(data);
      onSummaryRefresh();
      toast.success("Image removed from curated gallery");
    } catch (err) {
      toast.error(err.message || "Failed to remove image");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleHidden = async (image) => {
    if (togglingId) return;
    const nextHidden = !image.hidden;
    setTogglingId(image.id);
    try {
      const data = await fetcher(
        `/api/admin/curated-galleries/${hotel.hotel_slug}/images/${image.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ hidden: nextHidden }),
        }
      );
      setDoc(data);
      onSummaryRefresh();
      toast.success(nextHidden ? "Image hidden from public gallery" : "Image is visible again");
    } catch (err) {
      toast.error(err.message || "Failed to update visibility");
    } finally {
      setTogglingId(null);
    }
  };

  const handleReimport = async () => {
    if (reimporting) return;
    const confirmMsg =
      doc?.images?.length > 0
        ? `Re-import will REPLACE the current ${doc.images.length} curated images with a fresh clone from Bunny (${hotel.bunny_folder}).\n\nThis is destructive to the current curation. Bunny Storage itself is not affected.`
        : `Import all images from ${hotel.bunny_folder}?`;
    if (!window.confirm(confirmMsg)) return;
    setReimporting(true);
    try {
      const params = doc?.images?.length > 0 ? "?replace=true" : "";
      const data = await fetcher(
        `/api/admin/curated-galleries/${hotel.hotel_slug}/import${params}`,
        { method: "POST" }
      );
      setDoc(data);
      onSummaryRefresh();
      toast.success(`Imported ${data.imported} images from Bunny`);
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setReimporting(false);
    }
  };

  const handleClear = async () => {
    if (!doc?.images?.length) return;
    if (!window.confirm(`Clear all ${doc.images.length} curated images?\n\nBunny Storage will NOT be affected.`)) return;
    try {
      const data = await fetcher(
        `/api/admin/curated-galleries/${hotel.hotel_slug}`,
        { method: "DELETE" }
      );
      setDoc(data);
      onSummaryRefresh();
      toast.success("Curated gallery cleared");
    } catch (err) {
      toast.error(err.message || "Clear failed");
    }
  };

  // ── Selection helpers ────────────────────────────────────────────────
  const toggleSelect = useCallback((image) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(image.id)) next.delete(image.id);
      else next.add(image.id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Bulk endpoint — one round-trip for many ids so we never spam the
  // backend when the operator ticks 50 images.
  const runBulk = async (action, verb) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || bulkBusy) return;
    const label = ids.length === 1 ? "1 image" : `${ids.length} images`;
    if (action === "delete") {
      if (!window.confirm(`Delete ${label} from the curated gallery?\n\nBunny Storage will NOT be affected — originals stay intact.`)) return;
    }
    setBulkBusy(true);
    try {
      const data = await fetcher(
        `/api/admin/curated-galleries/${hotel.hotel_slug}/bulk`,
        { method: "POST", body: JSON.stringify({ action, image_ids: ids }) }
      );
      setDoc(data);
      onSummaryRefresh();
      clearSelection();
      toast.success(`${data.affected ?? 0} ${data.affected === 1 ? "image" : "images"} ${verb}`);
    } catch (err) {
      toast.error(err.message || `Bulk ${action} failed`);
    } finally {
      setBulkBusy(false);
    }
  };
  const handleBulkHide   = () => runBulk("hide",   "hidden");
  const handleBulkUnhide = () => runBulk("unhide", "unhidden");
  const handleBulkDelete = () => runBulk("delete", "deleted");

  // ── Drag-and-drop reorder ────────────────────────────────────────────
  // Entering reorder mode forces filter to "all" (partial reorders in a
  // filtered view would drop info the operator can't see), and clears
  // any active selection so nothing competes with the drag intent.
  const enterReorderMode = () => {
    setFilter("all");
    setSelectedIds(new Set());
    setReorderMode(true);
  };
  const exitReorderMode = () => {
    setReorderMode(false);
    setDraggingId(null);
    setDropTargetId(null);
  };

  const persistOrder = async (newOrderedIds) => {
    // Optimistic — mutate the local doc immediately so the tile settles
    // where the operator dropped it without waiting for the round-trip.
    const byId = new Map(images.map((img) => [img.id, img]));
    const optimistic = {
      ...doc,
      images: newOrderedIds.map((id) => byId.get(id)).filter(Boolean),
    };
    const previousDoc = doc;
    setDoc(optimistic);
    setSavingOrder(true);
    try {
      const data = await fetcher(
        `/api/admin/curated-galleries/${hotel.hotel_slug}/reorder`,
        { method: "PUT", body: JSON.stringify({ image_ids: newOrderedIds }) }
      );
      setDoc(data);
    } catch (err) {
      setDoc(previousDoc);
      toast.error(err.message || "Failed to save new order");
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragStart = (e, image) => {
    setDraggingId(image.id);
    try {
      e.dataTransfer.effectAllowed = "move";
      // Chrome needs *something* on the dataTransfer or the drop event
      // won't fire on nested divs.
      e.dataTransfer.setData("text/plain", image.id);
    } catch { /* Safari/older browsers — ignore. */ }
  };
  const handleDragEnter = (_e, image) => {
    if (draggingId && image.id !== draggingId) setDropTargetId(image.id);
  };
  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTargetId(null);
  };
  const handleDrop = async (e, targetImage) => {
    e.preventDefault();
    const srcId = draggingId;
    setDraggingId(null);
    setDropTargetId(null);
    if (!srcId || srcId === targetImage.id) return;

    // Compute the new order across the ENTIRE curated list — not just the
    // paginated visible window — so images beyond `visibleCount` keep
    // their existing positions relative to each other.
    const allIds = images.map((img) => img.id);
    const srcIdx = allIds.indexOf(srcId);
    const dstIdx = allIds.indexOf(targetImage.id);
    if (srcIdx === -1 || dstIdx === -1) return;
    const next = allIds.slice();
    next.splice(srcIdx, 1);
    // If dragging forward (srcIdx < dstIdx), the target shifted left by 1.
    const insertAt = srcIdx < dstIdx ? dstIdx - 1 : dstIdx;
    next.splice(insertAt, 0, srcId);
    if (next.join("|") === allIds.join("|")) return; // no change
    await persistOrder(next);
  };

  const images = doc?.images || [];
  const hiddenCount = images.filter((img) => img.hidden).length;
  const visibleCountPublic = images.length - hiddenCount;
  const filteredImages = useMemo(() => {
    if (filter === "visible") return images.filter((img) => !img.hidden);
    if (filter === "hidden") return images.filter((img) => img.hidden);
    return images;
  }, [images, filter]);
  const visible = filteredImages.slice(0, visibleCount);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filter]);

  // Selection derivatives
  const selectionCount = selectedIds.size;
  const selectionActive = selectionCount > 0;
  // Are all currently *visible* tiles selected? Governs the "Select all
  // on this page" master button — flips into an "Unselect all" when true.
  const allOnPageSelected = visible.length > 0 && visible.every((img) => selectedIds.has(img.id));
  const selectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        visible.forEach((img) => next.delete(img.id));
      } else {
        visible.forEach((img) => next.add(img.id));
      }
      return next;
    });
  };
  // How many of the selected images are currently hidden — controls which
  // of Hide/Unhide bulk buttons stays enabled.
  const selectedHiddenCount = images.filter(
    (img) => selectedIds.has(img.id) && img.hidden
  ).length;
  const selectedVisibleCount = selectionCount - selectedHiddenCount;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-[#5C5248] hover:text-[#2C2621] transition-colors mb-2"
            data-testid="curated-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back to all hotels
          </button>
          <h1 className="font-serif-x text-3xl md:text-4xl text-[#2C2621]">
            {hotel.hotel_slug}
          </h1>
          <p className="mt-1 text-[11px] tracking-[0.2em] uppercase text-[#5C5248]/80">
            Bunny folder: {hotel.bunny_folder} · {images.length} curated
            {hiddenCount > 0 ? (
              <span className="text-[#C16542]"> · {visibleCountPublic} visible · {hiddenCount} hidden</span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {images.length > 1 ? (
            <button
              type="button"
              onClick={reorderMode ? exitReorderMode : enterReorderMode}
              disabled={savingOrder}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors disabled:opacity-60 ${
                reorderMode
                  ? "bg-[#2C2621] text-white hover:bg-[#1a1613]"
                  : "border border-[#2C2621] text-[#2C2621] hover:bg-[#2C2621] hover:text-white"
              }`}
              data-testid="curated-reorder-toggle"
            >
              {savingOrder ? <Loader2 className="w-3 h-3 animate-spin" /> : <Move className="w-3 h-3" />}
              {reorderMode ? "Done" : "Reorder"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleReimport}
            disabled={reimporting || reorderMode}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.25em] uppercase bg-[#C16542] text-white hover:bg-[#A35133] disabled:opacity-60 transition-colors"
            data-testid="curated-reimport"
          >
            {reimporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {images.length > 0 ? "Re-import" : "Import from Bunny"}
          </button>
          {images.length > 0 && !reorderMode ? (
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.25em] uppercase border border-[#C16542] text-[#C16542] hover:bg-[#C16542] hover:text-white transition-colors"
              data-testid="curated-clear"
            >
              <Trash2 className="w-3 h-3" /> Clear all
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#5C5248] py-16 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading curated gallery…
        </div>
      ) : images.length === 0 ? (
        <div className="border border-dashed border-[#C16542]/50 bg-[#FDFBF7] p-10 text-center">
          <ImageOff className="w-8 h-8 text-[#C16542] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-[13px] text-[#2C2621] max-w-md mx-auto">
            No images yet. Click <strong>Import from Bunny</strong> to clone every
            image found under <code className="text-[11px] bg-[#F2EBE1] px-1 py-0.5">{hotel.bunny_folder}</code>.
            This is a one-time reference copy — after the import, changes to the
            curated gallery do <strong>not</strong> touch Bunny Storage.
          </p>
        </div>
      ) : (
        <>
          {/* Reorder mode banner — visible only while dragging is enabled,
              gives the operator a persistent hint about what's happening
              and how to leave the mode. */}
          {reorderMode ? (
            <div
              className="mb-4 flex items-center gap-3 px-4 py-3 bg-[#2C2621] text-white"
              data-testid="curated-reorder-banner"
            >
              <Move className="w-4 h-4 shrink-0" />
              <div className="text-[11px] leading-relaxed">
                <div className="tracking-[0.2em] uppercase">Reorder mode</div>
                <div className="text-white/70 mt-0.5">
                  Drag any tile onto another to move it. Order is saved automatically.
                  Selection · hide · delete are disabled until you exit.
                </div>
              </div>
              {savingOrder ? (
                <div className="ml-auto flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase" data-testid="curated-reorder-saving">
                  <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                </div>
              ) : null}
            </div>
          ) : (
            <div
              className="mb-4 flex items-center gap-2 flex-wrap"
              data-testid="curated-filter-bar"
            >
              {[
                { id: "all",     label: "All",          count: images.length },
                { id: "visible", label: "Visible only", count: visibleCountPublic },
                { id: "hidden",  label: "Hidden only",  count: hiddenCount },
              ].map((chip) => {
                const active = filter === chip.id;
                const disabled = chip.count === 0 && chip.id !== "all";
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => setFilter(chip.id)}
                    disabled={disabled}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase transition-colors ${
                      active
                        ? "bg-[#2C2621] text-white"
                        : "border border-[#E8DFCF] text-[#5C5248] hover:border-[#2C2621] hover:text-[#2C2621]"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid={`curated-filter-${chip.id}`}
                  >
                    {chip.label}
                    <span className={`text-[9px] ${active ? "opacity-80" : "opacity-70"}`}>· {chip.count}</span>
                  </button>
                );
              })}
              {filteredImages.length > 0 ? (
                <button
                  type="button"
                  onClick={selectAllOnPage}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase border border-[#E8DFCF] text-[#5C5248] hover:border-[#2C2621] hover:text-[#2C2621] transition-colors"
                  data-testid="curated-select-all-page"
                >
                  {allOnPageSelected ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                  {allOnPageSelected ? "Unselect page" : `Select page · ${visible.length}`}
                </button>
              ) : null}
            </div>
          )}

          {/* Bulk action bar — never shown while reordering. */}
          {selectionActive && !reorderMode ? (
            <div
              className="sticky top-0 z-20 -mx-6 md:-mx-10 px-6 md:px-10 py-3 mb-4 bg-[#2C2621] text-white flex items-center gap-3 flex-wrap"
              data-testid="curated-bulk-bar"
            >
              <div className="text-[11px] tracking-[0.24em] uppercase">
                <span data-testid="curated-bulk-count">{selectionCount}</span> selected
                {selectedHiddenCount > 0 || selectedVisibleCount > 0 ? (
                  <span className="ml-2 text-white/60">
                    · {selectedVisibleCount} visible · {selectedHiddenCount} hidden
                  </span>
                ) : null}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleBulkHide}
                  disabled={bulkBusy || selectedVisibleCount === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="curated-bulk-hide"
                >
                  {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <EyeOff className="w-3 h-3" />}
                  Hide
                </button>
                <button
                  type="button"
                  onClick={handleBulkUnhide}
                  disabled={bulkBusy || selectedHiddenCount === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="curated-bulk-unhide"
                >
                  {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                  Unhide
                </button>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase bg-[#C16542] hover:bg-[#A35133] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="curated-bulk-delete"
                >
                  {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Delete
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] tracking-[0.24em] uppercase text-white/70 hover:text-white transition-colors"
                  data-testid="curated-bulk-clear"
                >
                  <IconX className="w-3 h-3" /> Clear
                </button>
              </div>
            </div>
          ) : null}

          {filteredImages.length === 0 ? (
            <div className="border border-dashed border-[#E8DFCF] bg-[#FDFBF7] p-10 text-center text-[13px] text-[#5C5248]" data-testid="curated-filter-empty">
              No images match this filter.
            </div>
          ) : (
            <>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2"
                data-testid="curated-grid"
              >
                {visible.map((image) => (
                  <ImageTile
                    key={image.id}
                    image={image}
                    onDelete={handleDelete}
                    onToggleHidden={handleToggleHidden}
                    onToggleSelect={toggleSelect}
                    isDeleting={deletingId === image.id}
                    isToggling={togglingId === image.id}
                    isSelected={selectedIds.has(image.id)}
                    selectionActive={selectionActive}
                    reorderMode={reorderMode}
                    isDragging={draggingId === image.id}
                    isDropTarget={dropTargetId === image.id}
                    onDragStart={handleDragStart}
                    onDragEnter={handleDragEnter}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                  />
                ))}
              </div>
              {visibleCount < filteredImages.length ? (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-[10px] tracking-[0.3em] uppercase border border-[#2C2621] text-[#2C2621] hover:bg-[#2C2621] hover:text-white transition-colors"
                    data-testid="curated-load-more"
                  >
                    Load {Math.min(PAGE_SIZE, filteredImages.length - visibleCount)} more
                    &nbsp;·&nbsp; {visibleCount} / {filteredImages.length} shown
                  </button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Summary view
// ─────────────────────────────────────────────────────────────────────────
const SummaryView = ({ onOpen }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importingSlug, setImportingSlug] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetcher("/api/admin/curated-galleries");
      setRows(data.hotels || []);
    } catch (err) {
      toast.error(err.message || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (hotel) => {
    setImportingSlug(hotel.hotel_slug);
    try {
      const data = await fetcher(
        `/api/admin/curated-galleries/${hotel.hotel_slug}/import`,
        { method: "POST" }
      );
      toast.success(`Imported ${data.imported} images from Bunny`);
      await load();
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setImportingSlug(null);
    }
  };

  const totalImages = useMemo(() => rows.reduce((s, r) => s + (r.image_count || 0), 0), [rows]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
      {/* Local page header — kept inline so this page has zero external
          admin-component dependencies beyond the auth gate. */}
      <header className="mb-6">
        <h1 className="font-serif-x text-3xl md:text-4xl text-[#2C2621]">Curated Gallery</h1>
        <p className="mt-1 text-[13px] leading-relaxed text-[#5C5248] max-w-2xl">
          Per-accommodation image collections. Clone once from Bunny Storage,
          then curate independently — deletions here never affect the
          original Bunny assets.
        </p>
      </header>

      <div className="mb-4 text-[11px] tracking-[0.24em] uppercase text-[#5C5248]/80" data-testid="curated-total">
        {loading ? "Loading…" : `${totalImages.toLocaleString()} curated images across ${rows.length} accommodations`}
      </div>

      <div className="border border-[#E8DFCF] overflow-hidden">
        <table className="w-full bg-white">
          <thead className="bg-[#F2EBE1] text-[10px] tracking-[0.24em] uppercase text-[#5C5248]">
            <tr>
              <th className="px-4 py-2 text-left">Accommodation</th>
              <th className="px-4 py-2 text-center">Images</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="px-4 py-10 text-center text-[#5C5248]">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </td></tr>
            ) : rows.map((h) => (
              <SummaryRow
                key={h.hotel_slug}
                hotel={h}
                onOpen={onOpen}
                onImport={handleImport}
                isImporting={importingSlug === h.hotel_slug}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Outer page
// ─────────────────────────────────────────────────────────────────────────
const AdminCuratedGalleriesPage = () => {
  const [openHotel, setOpenHotel] = useState(null);
  const [summaryTick, setSummaryTick] = useState(0);

  return (
    <AdminAuthGate>
      <div className="min-h-screen bg-[#FDFBF7] pb-24" data-testid="admin-curated-galleries-page">
        {/* Breadcrumb — inlined here to avoid a new shared component just
            for this page. Follows the /admin visual language. */}
        <nav className="max-w-6xl mx-auto px-6 md:px-10 pt-6 text-[10px] tracking-[0.28em] uppercase text-[#5C5248]/70">
          <a href="/admin" className="hover:text-[#C16542] transition-colors">Admin</a>
          <span className="mx-2">/</span>
          <span className="text-[#2C2621]">Curated Gallery</span>
        </nav>
        {openHotel ? (
          <DetailView
            hotel={openHotel}
            onBack={() => setOpenHotel(null)}
            onSummaryRefresh={() => setSummaryTick((n) => n + 1)}
          />
        ) : (
          <SummaryView key={summaryTick} onOpen={setOpenHotel} />
        )}
      </div>
    </AdminAuthGate>
  );
};

export default AdminCuratedGalleriesPage;
